import React, { useState, useRef } from 'react';

const trackEvent = (eventName, params = {}) => {
  if (window.gtag) {
    window.gtag('event', eventName, { app: 'find_my_business', ...params });
  }
};

function QuizForm({ onSubmit }) {
  const hasStarted = useRef(false);
  const [formData, setFormData] = useState({
    experienceLevel: '',
    whatLightsYouUp: '',
    workStyle: '',
    productType: '',
    startupBudget: '',
    timeAvailable: '',
    naturalTalents: [],
    riskTolerance: '',
    problemsYouHate: [],
    whereYouHangOut: [],
    passionateTopic: '',
    problemPeopleAsk: ''
  });

  const handleChange = (e) => {
    if (!hasStarted.current) {
      hasStarted.current = true;
      trackEvent('quiz_started', { question_count: 12 });
    }
    const { name, value, type, checked } = e.target;
    
    if (type === 'checkbox') {
      setFormData(prev => ({
        ...prev,
        [name]: checked 
          ? [...prev[name], value]
          : prev[name].filter(item => item !== value)
      }));
    } else {
      setFormData({
        ...formData,
        [name]: value
      });
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    trackEvent('quiz_completed', { completion_time_sec: null });
    onSubmit(formData);
  };

  const isFormValid = () => {
    const allFieldsFilled = Object.entries(formData).every(([key, value]) => {
      if (key === 'naturalTalents' || key === 'problemsYouHate' || key === 'whereYouHangOut') {
        return Array.isArray(value) && value.length > 0;
      }
      return typeof value === 'string' ? value.trim() !== '' : true;
    });
    return allFieldsFilled;
  };

  return (
    <form onSubmit={handleSubmit} className="quiz-form">
      <div className="question-group">
        <label>1. What's your business experience level?</label>
        <select 
          name="experienceLevel" 
          value={formData.experienceLevel} 
          onChange={handleChange}
          required
        >
          <option value="">Select one...</option>
          <option value="never">Never started a business</option>
          <option value="sidehustler">Side hustler (1-2 ventures)</option>
          <option value="former">Former business owner</option>
          <option value="corporate">Corporate escapee ready to go solo</option>
        </select>
      </div>

      <div className="question-group">
        <label>2. What lights you up? What energizes you?</label>
        <select 
          name="whatLightsYouUp" 
          value={formData.whatLightsYouUp} 
          onChange={handleChange}
          required
        >
          <option value="">Select one...</option>
          <option value="helping">Helping people solve problems</option>
          <option value="creating">Creating things (physical or digital)</option>
          <option value="teaching">Teaching and sharing knowledge</option>
          <option value="building">Building systems and processes</option>
          <option value="connecting">Connecting people and ideas</option>
        </select>
      </div>

      <div className="question-group">
        <label>3. What's your ideal work style?</label>
        <select 
          name="workStyle" 
          value={formData.workStyle} 
          onChange={handleChange}
          required
        >
          <option value="">Select one...</option>
          <option value="solo">Solo - I work best alone</option>
          <option value="small-team">Small team (2-5 people)</option>
          <option value="client-facing">Client-facing - I love interacting with customers</option>
          <option value="behind-scenes">Behind the scenes - minimal client interaction</option>
        </select>
      </div>

      <div className="question-group">
        <label>4. What type of product/service appeals to you?</label>
        <select 
          name="productType" 
          value={formData.productType} 
          onChange={handleChange}
          required
        >
          <option value="">Select one...</option>
          <option value="physical">Physical products I can hold</option>
          <option value="digital">Digital products (courses, templates, tools)</option>
          <option value="services">Services (consulting, coaching, done-for-you)</option>
          <option value="hybrid">Hybrid (mix of products and services)</option>
        </select>
      </div>

      <div className="question-group">
        <label>5. How much startup capital do you have available?</label>
        <select 
          name="startupBudget" 
          value={formData.startupBudget} 
          onChange={handleChange}
          required
        >
          <option value="">Select one...</option>
          <option value="0-500">$0 - $500 (bootstrap mode)</option>
          <option value="500-2k">$500 - $2,000</option>
          <option value="2k-5k">$2,000 - $5,000</option>
          <option value="5k+">$5,000+ (ready to invest)</option>
        </select>
      </div>

      <div className="question-group">
        <label>6. How much time can you dedicate per week?</label>
        <select 
          name="timeAvailable" 
          value={formData.timeAvailable} 
          onChange={handleChange}
          required
        >
          <option value="">Select one...</option>
          <option value="5hrs">5 hours/week (evenings only)</option>
          <option value="10-20hrs">10-20 hours/week (serious side hustle)</option>
          <option value="fulltime">Full-time (40+ hours/week)</option>
          <option value="weekends">Weekends only</option>
        </select>
      </div>

      <div className="question-group">
        <label>7. What are you naturally good at? (Select all that apply)</label>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', marginTop: '0.5rem' }}>
          {[
            { value: 'talking', label: 'Talking to people / Sales / Networking' },
            { value: 'writing', label: 'Writing / Copywriting / Content creation' },
            { value: 'design', label: 'Design / Visual creativity' },
            { value: 'tech', label: 'Tech / Coding / Building tools' },
            { value: 'organization', label: 'Organization / Project management' },
            { value: 'strategy', label: 'Strategy / Big picture thinking' },
            { value: 'analysis', label: 'Data analysis / Research' }
          ].map(option => (
            <label key={option.value} style={{ 
              display: 'flex', 
              alignItems: 'center', 
              cursor: 'pointer',
              padding: '0.75rem',
              background: formData.naturalTalents.includes(option.value) ? 'rgba(0, 210, 255, 0.1)' : 'rgba(255, 255, 255, 0.05)',
              borderRadius: '8px',
              border: formData.naturalTalents.includes(option.value) ? '2px solid #00d2ff' : '2px solid transparent',
              transition: 'all 0.2s ease'
            }}>
              <input
                type="checkbox"
                name="naturalTalents"
                value={option.value}
                checked={formData.naturalTalents.includes(option.value)}
                onChange={handleChange}
                style={{ marginRight: '0.75rem', cursor: 'pointer' }}
              />
              <span>{option.label}</span>
            </label>
          ))}
        </div>
      </div>

      <div className="question-group">
        <label>8. What's your risk tolerance?</label>
        <select 
          name="riskTolerance" 
          value={formData.riskTolerance} 
          onChange={handleChange}
          required
        >
          <option value="">Select one...</option>
          <option value="safe">Play it safe - proven models only</option>
          <option value="calculated">Calculated risks are fine</option>
          <option value="adventurous">I love jumping into new trends</option>
        </select>
      </div>

      <div className="question-group">
        <label>9. Which of these do you personally HATE dealing with? (Select all that apply)</label>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', marginTop: '0.5rem' }}>
          {[
            { value: 'inventory', label: 'Inventory / Shipping / Physical products' },
            { value: 'customer-service', label: 'Customer service calls / Support tickets' },
            { value: 'tech-setup', label: 'Tech setup / Troubleshooting' },
            { value: 'appointments', label: 'Booking appointments / Scheduling' },
            { value: 'content', label: 'Creating new content constantly' },
            { value: 'none', label: 'None - I don\'t mind any of these!' }
          ].map(option => (
            <label key={option.value} style={{ 
              display: 'flex', 
              alignItems: 'center', 
              cursor: 'pointer',
              padding: '0.75rem',
              background: formData.problemsYouHate.includes(option.value) ? 'rgba(242, 5, 85, 0.1)' : 'rgba(255, 255, 255, 0.05)',
              borderRadius: '8px',
              border: formData.problemsYouHate.includes(option.value) ? '2px solid #f20555' : '2px solid transparent',
              transition: 'all 0.2s ease'
            }}>
              <input
                type="checkbox"
                name="problemsYouHate"
                value={option.value}
                checked={formData.problemsYouHate.includes(option.value)}
                onChange={handleChange}
                style={{ marginRight: '0.75rem', cursor: 'pointer' }}
              />
              <span>{option.label}</span>
            </label>
          ))}
        </div>
      </div>

      <div className="question-group">
        <label>10. Where do you naturally spend your free time online? (Select all that apply)</label>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', marginTop: '0.5rem' }}>
          {[
            { value: 'instagram', label: 'Instagram' },
            { value: 'tiktok', label: 'TikTok' },
            { value: 'youtube', label: 'YouTube' },
            { value: 'pinterest', label: 'Pinterest' },
            { value: 'reddit', label: 'Reddit' },
            { value: 'facebook', label: 'Facebook groups' },
            { value: 'linkedin', label: 'LinkedIn' },
            { value: 'offline', label: 'Nowhere - I\'m offline a lot' }
          ].map(option => (
            <label key={option.value} style={{ 
              display: 'flex', 
              alignItems: 'center', 
              cursor: 'pointer',
              padding: '0.75rem',
              background: formData.whereYouHangOut.includes(option.value) ? 'rgba(0, 210, 255, 0.1)' : 'rgba(255, 255, 255, 0.05)',
              borderRadius: '8px',
              border: formData.whereYouHangOut.includes(option.value) ? '2px solid #00d2ff' : '2px solid transparent',
              transition: 'all 0.2s ease'
            }}>
              <input
                type="checkbox"
                name="whereYouHangOut"
                value={option.value}
                checked={formData.whereYouHangOut.includes(option.value)}
                onChange={handleChange}
                style={{ marginRight: '0.75rem', cursor: 'pointer' }}
              />
              <span>{option.label}</span>
            </label>
          ))}
        </div>
      </div>

      <div className="question-group">
        <label>11. What topic could you talk about for hours? (BE SPECIFIC!)</label>
        <p style={{ 
          fontSize: '0.9rem', 
          color: '#f20555', 
          marginBottom: '0.5rem',
          fontWeight: '600'
        }}>
          ⚠️ Don't just say "marketing" or "legal" - be WAY more specific!
        </p>
        <textarea
          name="passionateTopic"
          value={formData.passionateTopic}
          onChange={handleChange}
          placeholder="❌ BAD: 'legal advice' or 'marketing'
✅ GOOD: 'Workers comp claims for injured construction workers' or 'Instagram marketing for small bakeries'
✅ GOOD: 'Helping busy moms meal prep on a budget' or 'Teaching seniors how to use smartphones'"
          rows="4"
          required
        />
      </div>

      <div className="question-group">
        <label>12. What SPECIFIC problem do people always ask you for help with?</label>
        <p style={{ 
          fontSize: '0.9rem', 
          color: '#f20555', 
          marginBottom: '0.5rem',
          fontWeight: '600'
        }}>
          ⚠️ The more specific, the better your results!
        </p>
        <textarea
          name="problemPeopleAsk"
          value={formData.problemPeopleAsk}
          onChange={handleChange}
          placeholder="❌ BAD: 'tech help' or 'getting organized'
✅ GOOD: 'Fixing their iPhone when it's running slow' or 'Setting up their home office for remote work'
✅ GOOD: 'How to get their first 100 followers on Instagram' or 'Negotiating a raise at their corporate job'"
          rows="4"
          required
        />
      </div>

      <button 
        type="submit" 
        className="submit-btn"
        disabled={!isFormValid()}
      >
        Find My Perfect Business →
      </button>
    </form>
  );
}

export default QuizForm;